<template>
    <div class="user-gender">
        <Card size="small">
            <header>
                <div class="name">{{ $t('venue_statistics.user_gender.headline') }}</div>
                <Select v-model="select" :options="options"></Select>
            </header>
            <div v-if="showEmptyState" class="empty-state">
                <p>
                    {{ $t('venue_statistics.user_gender.empty_state') }}
                </p>
            </div>
            <div v-else class="chart-content">
                <DDoughnut :items="chartData" :colors="['#0399DA', '#FF427E', '#FEC400', '#9697A1']" />
                <div v-if="venueAnalytics" class="chart-legend">
                    <div class="item male">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.genderDistribution.male) }}
                        </h2>
                        <p>{{ $t('venue_statistics.user_gender.male') }}</p>
                    </div>
                    <div class="item women">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.genderDistribution.female) }}
                        </h2>
                        <p>{{ $t('venue_statistics.user_gender.female') }}</p>
                    </div>
                    <div class="item unspecified">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.genderDistribution.unspecified) }}
                        </h2>
                        <p>{{ $t('venue_statistics.user_gender.unspecified') }}</p>
                    </div>
                    <div class="item diverse">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.genderDistribution.diverse) }}
                        </h2>
                        <p>{{ $t('venue_statistics.user_gender.diverse') }}</p>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>

<script>
import Card from '@/components/Card'
import Select from '@/components/Select'
import gql from 'graphql-tag'
import DDoughnut from './DDoughnut'

export default {
    name: 'UserGender',
    components: {
        Card,
        Select,
        DDoughnut,
    },
    props: {
        venueAnalytics: {
            type: Object,
            default: undefined,
        },
        historicalData: {
            type: Boolean,
            default: false,
        },
    },
    apollo: {
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        capacity
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
        },
    },
    data() {
        return {
            options: [
                { value: 'percent', text: '%' },
                { value: 'sum', text: 'Sum' },
            ],
            select: 'sum',
            name: 'user-gender-chart',
            isMounting: true,
            value: 5,
            strokeWidth: 5,
            radius: 61,
        }
    },
    computed: {
        offset() {
            return this.circumference - (this.value / 100) * this.circumference
        },
        circumference() {
            return this.radius * 2 * Math.PI
        },
        showEmptyState() {
            return !(
                this.venueAnalytics &&
                this.venueAnalytics.visitors &&
                this.venueAnalytics.visitors.genderDistribution &&
                this.venueAnalytics.visitors.genderDistribution.male +
                    this.venueAnalytics.visitors.genderDistribution.female +
                    this.venueAnalytics.visitors.genderDistribution.diverse +
                    this.venueAnalytics.visitors.genderDistribution.unspecified >
                    0
            )
        },
        chartData() {
            let data = [0, 0, 0, 0]

            const a = this.venueAnalytics.visitors.genderDistribution
            const total = a.male + a.female + a.diverse + a.unspecified
            data = [a.male, a.female, a.diverse, a.unspecified]
            data = data.map((el) => el / total)

            return data

            // return {
            //     type: 'doughnut',
            //     labels: ['male', 'female', 'diverse', 'unspecified'],
            //     data: {
            //         datasets: [
            //             {
            //                 data,
            //                 backgroundColor: [
            //                     'rgba(3, 218, 197, 1)',
            //                     'rgba(255, 66, 126, 1)',
            //                     'rgba(254, 196, 0, 1)',
            //                     'rgba(231, 233, 237, 1)',
            //                 ],
            //             },
            //         ],
            //     },
            //     plugins: {
            //         legend: false,
            //         tooltip: false,
            //     },
            //     options: {
            //         responsive: true,
            //         borderWidth: 0,
            //         cutout: 50,
            //         animation: {
            //             duration: 0,
            //         },
            //     },
            // }
        },
    },
    methods: {
        occurrenceFormatted(occurrence) {
            if (!occurrence) {
                return 0
            }

            if (this.select === 'percent') {
                const cV = this.venueAnalytics.visitors
                const total = cV.new + cV.recurring + cV.vip
                return `${Math.round((occurrence / total) * 100)} %`
            }

            return occurrence
        },
    },
}
</script>

<style scoped lang="sass">
.user-gender
    max-width: 100vw
    width: 100%
    box-sizing: border-box

    .empty-state
        display: flex
        justify-content: center
        align-items: center
        height: 100px
        text-align: center
        user-select: none

    .card
        min-height: 229px
        box-sizing: border-box
        background: #ffffff !important
    header
        display: flex
        justify-content: space-between
        margin-bottom: $l

        .d-select
            min-width: 80px

        .name
            color: $neutral-black
            font-weight: 600
            padding: $xs
            font-size: 14px
            line-height: 17px

    .chart-content
        width: 445px
        display: flex

        .chart-legend
            display: grid
            grid-column-gap: 18px
            grid-row-gap: 14px
            grid-template-columns: 55px 55px
            margin-left: 34px

            .item
                margin: 0

                &.male h2
                    color: #0399DA

                &.women h2
                    color: #FF427E

                &.diverse h2
                    color: #FEC400

                &.unspecified h2
                    color: #9697A1

                h2
                    margin-bottom: 2px
                    font-weight: bold
                    font-size: 22px
                    line-height: 28px
                    letter-spacing: 0.5px

                p
                    margin: 0
                    padding: 0
                    font-size: 12px
                    line-height: 16px
                    color: $neutral-3

        .chart-container
            width: 122px
</style>
